import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import baseline from "@/i18n/hy.json";

const I18nContext = createContext(null);
const ATTRS = ["placeholder", "title", "aria-label", "alt"];

export const isDeveloper = (user) => user?.role === "developer" || (user?.roles || []).includes("developer");

// Site is Armenian-only; developers may switch to English. Words come from the code baseline, overridden by the DB.
export function I18nProvider({ children }) {
  const { user } = useAuth();
  const canToggle = isDeveloper(user);
  const [devLang, setDevLang] = useState(() => localStorage.getItem("scout_lang") || "hy");
  const [dbDict, setDbDict] = useState({});
  const [ready, setReady] = useState(false);
  const lang = canToggle ? devLang : "hy";
  const dict = useMemo(() => ({ ...baseline, ...dbDict }), [dbDict]);

  const refresh = useCallback(() => {
    api.get("/public/translations")
      .then(r => setDbDict(r.data || {}))
      .catch(err => console.warn("translations load failed:", err?.message))
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    refresh();
    const onUpdate = () => refresh();
    window.addEventListener("translations-updated", onUpdate);
    return () => window.removeEventListener("translations-updated", onUpdate);
  }, [refresh]);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const setLang = (l) => {
    setDevLang(l);
    try { localStorage.setItem("scout_lang", l); } catch (err) { /* noop */ }
    if (l === "en") window.location.reload();
  };

  const t = (en, hy) => {
    if (lang !== "hy") return en;
    return dbDict[en] || hy || baseline[en] || en;
  };

  return (
    <I18nContext.Provider value={{ lang, setLang, canToggle, t, dict, ready, refresh }}>
      {children}
      {lang === "hy" && <AutoTranslate dict={dict} />}
    </I18nContext.Provider>
  );
}

// Translates any remaining hard-coded English text on screen using the dictionary.
function AutoTranslate({ dict }) {
  useEffect(() => {
    const fixText = (node) => {
      const v = node.nodeValue;
      const k = v && v.trim();
      if (k && dict[k] && dict[k] !== k) node.nodeValue = v.replace(k, dict[k]);
    };
    const fixEl = (el) => {
      if (el.closest && el.closest("[data-no-translate], textarea, input[type=text]:not([placeholder]), code")) return;
      ATTRS.forEach(a => {
        const v = el.getAttribute && el.getAttribute(a);
        if (v && dict[v.trim()]) el.setAttribute(a, dict[v.trim()]);
      });
    };
    const walk = (root) => {
      if (root.nodeType === 3) { if (!root.parentElement?.closest("[data-no-translate], textarea")) fixText(root); return; }
      if (root.nodeType !== 1) return;
      fixEl(root);
      const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
      let n = tw.nextNode();
      while (n) {
        if (n.nodeType === 3) { if (!n.parentElement?.closest("[data-no-translate], textarea, script, style")) fixText(n); }
        else fixEl(n);
        n = tw.nextNode();
      }
    };
    walk(document.body);
    const obs = new MutationObserver((muts) => {
      obs.disconnect();
      muts.forEach(m => {
        if (m.type === "characterData") fixText(m.target);
        else if (m.type === "attributes") fixEl(m.target);
        else m.addedNodes.forEach(walk);
      });
      obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    });
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    return () => obs.disconnect();
  }, [dict]);
  return null;
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    return { lang: "hy", setLang: () => {}, canToggle: false, t: (en, hy) => hy || baseline[en] || en, dict: baseline, ready: true, refresh: () => {} };
  }
  return ctx;
}
